<template>
  <div class="model-3d-viewer">
    <div class="viewer-header">
      <div class="model-info">
        <span class="model-label">当前模型:</span>
        <span class="model-name">{{ getModelDisplayName(currentModel) }}</span>
      </div>
      <div class="viewer-controls">
        <el-button-group size="mini">
          <el-button @click="resetCamera" icon="el-icon-refresh">重置视角</el-button>
          <el-button @click="toggleRotation" :type="autoRotate ? 'primary' : ''">
            {{ autoRotate ? '停止旋转' : '自动旋转' }}
          </el-button>
        </el-button-group>
      </div>
    </div>

    <div
      ref="container"
      class="viewer-container"
      v-loading="loading"
      element-loading-text="加载3D模型中..."
    >
      <div v-if="error" class="error-message">
        <i class="el-icon-warning"></i>
        <p>{{ error }}</p>
        <el-button size="small" @click="loadModel">重试</el-button>
      </div>
    </div>

    <div class="viewer-info">
      <div class="info-item">
        <span class="label">顶点数:</span>
        <span class="value">{{ modelStats.vertices }}</span>
      </div>
      <div class="info-item">
        <span class="label">面数:</span>
        <span class="value">{{ modelStats.faces }}</span>
      </div>
      <div class="info-item">
        <span class="label">体积:</span>
        <span class="value">{{ modelStats.volume }}</span>
      </div>
    </div>

    <div class="viewer-tips">
      <p><i class="el-icon-info"></i> 鼠标左键拖动旋转，滚轮缩放，右键拖动平移</p>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { check3DModels, get3DModelUrl } from '@/api';

export default {
  name: 'Model3DViewer',
  props: {
    patient: {
      type: String,
      required: true
    },
    date: {
      type: String,
      required: true
    },
    ownerId: {
      type: [String, Number],
      default: null
    },
    selectedMaskType: {
      type: String,
      default: 'psoas'
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      currentMesh: null,
      autoRotate: false,
      animationId: null,
      currentModel: this.selectedMaskType || 'psoas', // 使用传入的prop作为初始值
      modelStats: {
        vertices: 0,
        faces: 0,
        volume: '-'
      }
    };
  },
  watch: {
    selectedMaskType(newVal) {
      // 当外部传入的maskType改变时，更新currentModel并重新加载
      if (newVal && newVal !== this.currentModel) {
        this.currentModel = newVal;
        this.loadModel();
      }
    }
  },
  mounted() {
    this.initScene();
    this.loadModel();
    window.addEventListener('resize', this.onWindowResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.renderer) {
      this.renderer.dispose();
    }
  },
  methods: {
    initScene() {
      const container = this.$refs.container;
      if (!container) return;

      const width = container.clientWidth;
      const height = container.clientHeight || 500;

      // 创建场景
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xf5f5f5);

      // 创建相机
      this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 10000);
      this.camera.position.set(0, 0, 300);

      // 创建渲染器
      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      container.appendChild(this.renderer.domElement);

      // 添加控制器
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
      this.controls.screenSpacePanning = false;
      this.controls.minDistance = 50;
      this.controls.maxDistance = 1000;

      // 添加灯光
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      this.scene.add(ambientLight);

      const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight1.position.set(1, 1, 1);
      this.scene.add(directionalLight1);

      const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
      directionalLight2.position.set(-1, -1, -1);
      this.scene.add(directionalLight2);

      // 添加网格辅助线
      const gridHelper = new THREE.GridHelper(400, 20, 0xcccccc, 0xe0e0e0);
      this.scene.add(gridHelper);

      // 开始渲染循环
      this.animate();
    },
    animate() {
      this.animationId = requestAnimationFrame(this.animate);

      if (this.autoRotate && this.currentMesh) {
        this.currentMesh.rotation.y += 0.005;
      }

      this.controls.update();
      this.renderer.render(this.scene, this.camera);
    },
    async loadModel() {
      console.log('[3D加载] 开始加载模型...', {
        patient: this.patient,
        date: this.date,
        currentModel: this.currentModel
      });

      this.loading = true;
      this.error = null;

      try {
        // 移除旧模型
        if (this.currentMesh) {
          this.scene.remove(this.currentMesh);
          if (this.currentMesh.geometry) {
            this.currentMesh.geometry.dispose();
          }
          if (this.currentMesh.material) {
            this.currentMesh.material.dispose();
          }
        }

        console.log('[3D加载] 加载后端模型文件...');
        const modelInfo = await this.findCurrentModelFile();
        const modelUrl = get3DModelUrl(this.patient, this.date, modelInfo.filename, this.ownerId);
        const loader = this.getLoader(modelUrl);
        const loadedModel = await this.loadModelFile(loader, modelUrl);

        // 加载并居中模型
        this.currentMesh = this.prepareLoadedModel(loadedModel);

        // 创建网格
        this.scene.add(this.currentMesh);

        // 更新统计信息
        this.updateModelStatsFromObject(this.currentMesh, modelInfo);

        // 调整相机位置以适应模型
        this.fitCameraToModel();

        console.log('[3D加载] 模型加载完成');
        this.$message.success('3D模型加载成功');
      } catch (err) {
        console.error('[3D加载] 加载3D模型失败:', err);
        this.error = err.message || '加载3D模型失败，请检查模型文件是否存在';
        this.$message.error(this.error);
      } finally {
        this.loading = false;
      }
    },
    getLoader(url) {
      // 移除查询参数，只检查文件扩展名
      const urlWithoutQuery = url.split('?')[0];

      if (urlWithoutQuery.endsWith('.stl')) {
        return new STLLoader();
      } else if (urlWithoutQuery.endsWith('.obj')) {
        return new OBJLoader();
      }
      throw new Error('不支持的模型格式');
    },
    loadModelFile(loader, url) {
      return new Promise((resolve, reject) => {
        loader.load(
          url,
          (geometry) => {
            if (geometry.isBufferGeometry) {
              resolve(geometry);
            } else if (geometry.children && geometry.children.length > 0) {
              // OBJ可能返回Group
              resolve(geometry.children[0].geometry);
            } else {
              reject(new Error('无效的几何体'));
            }
          },
          (progress) => {
            if (progress.lengthComputable) {
              const percent = (progress.loaded / progress.total) * 100;
              console.log(`加载进度: ${percent.toFixed(2)}%`);
            }
          },
          (error) => {
            reject(error);
          }
        );
      });
    },
    async findCurrentModelFile() {
      const result = await check3DModels(this.patient, this.date, this.ownerId);
      const models = result && result.models ? result.models : [];
      const model = models.find((item) => (
        item.mask_type === this.currentModel ||
        item.type === this.currentModel ||
        (item.filename || '').toLowerCase().includes(this.currentModel)
      ));
      if (!model) {
        throw new Error('暂无当前类型的3D模型文件');
      }
      return model;
    },
    prepareLoadedModel(loadedModel) {
      const material = new THREE.MeshPhongMaterial({
        color: this.getModelColor(this.currentModel),
        specular: 0x111111,
        shininess: 200,
        side: THREE.DoubleSide
      });

      let object;
      if (loadedModel.isBufferGeometry) {
        loadedModel.center();
        loadedModel.computeBoundingBox();
        loadedModel.computeVertexNormals();
        object = new THREE.Mesh(loadedModel, material);
      } else {
        object = loadedModel;
        object.traverse((child) => {
          if (child.isMesh) {
            child.material = material;
            if (child.geometry) {
              child.geometry.computeBoundingBox();
              child.geometry.computeVertexNormals();
            }
          }
        });
      }

      const box = new THREE.Box3().setFromObject(object);
      const center = box.getCenter(new THREE.Vector3());
      object.position.sub(center);
      return object;
    },
    getModelColor(modelType) {
      const colors = {
        psoas: 0xff6b6b,      // 红色 - 腰大肌
        muscle: 0x4ecdc4,     // 青色 - 全肌肉
        vertebra: 0xf7fff7    // 白色 - 椎骨
      };
      return colors[modelType] || 0xff6b6b;
    },
    getModelDisplayName(modelType) {
      const names = {
        psoas: '腰大肌',
        muscle: '全肌肉',
        vertebra: '椎骨'
      };
      return names[modelType] || '未知';
    },
    updateModelStatsFromObject(object, modelInfo = {}) {
      let vertices = 0;
      let faces = 0;
      object.traverse((child) => {
        if (!child.isMesh || !child.geometry || !child.geometry.attributes.position) return;
        const geometry = child.geometry;
        const count = geometry.attributes.position.count;
        vertices += count;
        faces += geometry.index ? geometry.index.count / 3 : count / 3;
      });

      this.modelStats = {
        vertices: vertices.toLocaleString(),
        faces: Math.floor(faces).toLocaleString(),
        volume: modelInfo.volume_ml != null
          ? `${Number(modelInfo.volume_ml).toFixed(2)} mL`
          : '-'
      };
    },
    fitCameraToModel() {
      if (!this.currentMesh) return;

      const box = new THREE.Box3().setFromObject(this.currentMesh);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());

      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = this.camera.fov * (Math.PI / 180);
      let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
      cameraZ *= 1.5; // 增加一些边距

      this.camera.position.set(center.x, center.y, center.z + cameraZ);
      this.camera.lookAt(center);
      this.controls.target.copy(center);
      this.controls.update();
    },
    resetCamera() {
      this.camera.position.set(0, 0, 300);
      this.camera.lookAt(0, 0, 0);
      this.controls.target.set(0, 0, 0);
      this.controls.update();

      if (this.currentMesh) {
        this.fitCameraToModel();
      }
    },
    toggleRotation() {
      this.autoRotate = !this.autoRotate;
    },
    onWindowResize() {
      const container = this.$refs.container;
      if (!container) return;

      const width = container.clientWidth;
      const height = container.clientHeight;

      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    },
    async clientSide3DReconstruction() {
      console.log('[客户端3D] 开始浏览器端重建');

      // 动态导入Client3DReconstructor
      const { Client3DReconstructor } = await import('@/utils/client3DReconstructor');
      const { getMaskImages, getL3ImageUrl } = await import('@/api');

      // 1. 获取mask图像列表
      console.log('[客户端3D] 获取mask图像列表...');
      const maskData = await getMaskImages(this.patient, this.date, this.currentModel);

      if (!maskData.available || maskData.images.length === 0) {
        throw new Error('没有可用的mask图像');
      }

      console.log(`[客户端3D] 找到 ${maskData.count} 张mask图像`);

      // 2. 构建mask图像URL列表 - 使用getL3ImageUrl因为它支持get_output_image端点
      const maskUrls = maskData.images.map(filename => {
        return getL3ImageUrl(this.patient, this.date, maskData.folder, filename);
      });

      // 3. 创建重建器并执行重建
      const reconstructor = new Client3DReconstructor();

      // CT spacing - 根据实际情况调整
      const spacing = {
        dx: 0.734375,  // X轴spacing (mm)
        dy: 0.734375,  // Y轴spacing (mm)
        dz: 3.0        // Z轴spacing (mm)
      };

      console.log('[客户端3D] 开始重建，spacing:', spacing);

      const geometry = await reconstructor.reconstruct(
        maskUrls,
        spacing,
        (percent, message) => {
          console.log(`[客户端3D] ${percent.toFixed(0)}% - ${message}`);
          // 可以在这里更新loading文本显示进度
        }
      );

      console.log('[客户端3D] 重建完成!');
      return geometry;
    }
  }
};
</script>

<style scoped>
.model-3d-viewer {
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
}

.viewer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.model-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.model-label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.model-name {
  font-size: 15px;
  color: #0f172a;
  font-weight: 700;
  padding: 4px 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 6px;
}

.viewer-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.viewer-container {
  width: 100%;
  height: 500px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
  position: relative;
}

.error-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #f56c6c;
}

.error-message i {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-message p {
  margin: 0 0 16px 0;
  font-size: 14px;
}

.viewer-info {
  display: flex;
  gap: 20px;
  margin-top: 16px;
  padding: 12px;
  background: #f6f9ff;
  border-radius: 8px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-item .label {
  color: #666;
  font-size: 13px;
}

.info-item .value {
  color: #0f172a;
  font-weight: 600;
  font-size: 14px;
}

.viewer-tips {
  margin-top: 12px;
  padding: 8px 12px;
  background: #fff9e6;
  border-radius: 6px;
  border-left: 3px solid #ffc107;
}

.viewer-tips p {
  margin: 0;
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
}

.viewer-tips i {
  color: #ffc107;
}
</style>
